import * as addressLookup from '../../services/address-lookup.js';
import * as analytics from '../../services/analytics.js';
import * as audioRecorder from '../../services/audio-recorder.js';
import * as checkoutApi from '../../services/checkout-api.js';
import * as dateTime from '../../services/date-time.js';
import * as delivery from '../../services/delivery.js';
import * as formInput from '../../services/form-input.js';
import * as orderSubmission from '../../services/order-submission.js';
import * as stripe from '../../services/stripe.js';
import { createTotalManager } from '../../services/totals.js';

export default () => ({
    audioRecorder: {
        durationFormatted: '00:00',
        ending: false,
        errors: [],
        state: 'init',
    },

    checkout: {
        addressFieldsVisible: false,
        admin: false,
        expired: false,
        failed: { data: { messages: [], next_url: null, status: '' }, status: false },
        form: null,
        paymentMethod: 'card_gifted',
        placementStage: 'placing',
        postageOption: { id: null, name: '' },
        promoCode: { error: '', pending: false, value: '' },
        ready: true,
        session: null,
        showAddressFields: true,
        stripe: { confirmationToken: '', errorMessage: '', paymentIntent: '', paymentMethod: null },
        termsAccepted: false,
        token: '',
        totalUpdating: false,
    },

    contact: {
        email: '',
        firstName: '',
        lastName: '',
        telephone: '',
        title: '',
    },

    currentStep: null,

    delivery: {
        address: '',
        address1: '',
        address2: '',
        address3: '',
        country: '',
        county: '',
        firstName: '',
        lastName: '',
        postcode: '',
        town: '',
    },

    notes: '',

    productNotes: '',

    room: {
        arrivalDate: '',
        bookingReference: '',
        guestName: '',
        number: '',
    },

    packagingError: false,

    order: {
        bulk: false,
        combinePackagedVouchers: false,
        combinedPackagingId: 0,
        fulfilledByHotel: false,
        itemsToCollect: 0,
        itemsToEmail: 0,
        itemsToFulfilInHouse: 0,
        itemsToPackage: 0,
        itemsToPost: 0,
        itemsToRoom: 0,
        packagedItemsToPrint: 0,
        placed: false,
        requiresCollection: false,
        requiresPackaging: false,
        requiresPosting: false,
        requiresRoomDelivery: false,
        shippingOverseas: false,
    },

    originalTotals: {
        emailDelivery: null,
        full: null,
        items: null,
        packaging: null,
        postage: null,
    },

    settings: {},

    steps: [
        { name: 'Delivery', valid: false },
        { name: 'Personalise', valid: false },
        { name: 'Billing', valid: false },
        { name: 'Review', valid: false },
    ],

    totals: {
        discount: -1,
        discountFormatted: '',
        emailDelivery: -1,
        emailDeliveryFormatted: '',
        full: -1,
        fullFormatted: '',
        items: -1,
        itemsFormatted: '',
        packaging: -1,
        packagingFormatted: '',
        postage: -1,
        postageFormatted: '',
        postagePackaging: -1,
        postagePackagingFormatted: '',
    },

    userCan: {
        adjustTotals: false,
        changeDeliveryDate: false,
        changeStartDate: false,
        orderLuxuryVouchers: false,
        recordAudio: false,
        useAddressLookup: false,
    },

    vouchers: {},

    _expiryInterval: null,

    init() {
        const gifted = window.Gifted;

        this.checkout.admin = gifted.isAdminCheckout || false;
        this.checkout.form = this.$root.querySelector('.js-checkout-form') || this.$root.querySelector('form');
        this.checkout.session = gifted.checkoutSession;
        this.checkout.token = gifted.checkoutToken;
        if (!gifted.canPersonalise) {
            this.steps = this.steps.filter(step => step.name !== 'Personalise');
        }

        this.currentStep = this.steps[0];
        this.order.fulfilledByHotel = gifted.isSelfFulfilling || false;

        this.delivery.country = gifted.settings.default_country || '';

        this.settings = {
            deliveryCountries: gifted.settings.delivery_countries,
            domesticCountries: gifted.settings.domestic_countries,
            packagingLimit: gifted.settings.packaging_limit,
            packagingOptions: gifted.settings.packaging_options,
            postageOptions: gifted.settings.postage_options,
            scheduledDeliveryMaxDays: gifted.settings.scheduled_delivery_max_days,
            selfFulfilCountries: gifted.settings.self_fulfil_countries,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            voucherMessageLimit: gifted.settings.voucher_message_limit,
        };

        this._totalManager = createTotalManager({
            getEmail: () => this.contact.email,
            getForm: () => this.checkout.form,
            getSessionId: () => this.checkout.session.id,
            getSessionType: () => this.checkout.session.type,
            getStripeAmount: (totals) => totals.full,
            onPromoError: (error) => { this.checkout.promoCode.error = error; },
            onUpdate: (totals) => {
                this.checkout.totalUpdating = false;
                this.totals = totals;

                this.originalTotals = {
                    emailDelivery: totals.emailDelivery,
                    full: this.originalTotals.full ?? totals.full,
                    items: this.originalTotals.items ?? totals.items,
                    packaging: this.originalTotals.packaging ?? totals.packaging,
                    postage: this.originalTotals.postage ?? totals.postage,
                };

                this.getPostageOption(this.checkout.postageOption?.id);
            },
        });

        this.userCan = {
            adjustTotals: gifted.canAlterTotals || false,
            changeDeliveryDate: gifted.hasLuxuryDigitalVouchers || false,
            changeStartDate: gifted.canChangeStartDate || false,
            orderLuxuryVouchers: gifted.hasLuxuryDigitalVouchers || false,
            recordAudio: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
            useAddressLookup: gifted.addressLookup?.enabled || false,
        };

        (gifted.basket || []).forEach(item => {
            this.vouchers[`voucher${item.key}`] = {
                audio_path: '',
                audio_url: '',
                bulk_validity: '',
                custom_redemption: false,
                delivery: item.delivery_method,
                delivery_date: '',
                delivery_time: '',
                email: '',
                key: item.key,
                message: { 0: '', 1: '', 2: '', 3: '', 4: '' },
                name: '',
                option_id: item.option_id,
                packaging: 0,
                packaging_id: 0,
                packaging_option_ids: item.packaging_option_ids || [],
                personalised: false,
                prefix: '',
                reward_offer_id: item.reward_offer_id,
                type: item.type,
                valid_at: '',
                voucher_id: item.voucher_id,
                voucher_style: gifted.hasLuxuryDigitalVouchers ? 'luxury' : 'basic',
            };
        });

        this.checkout.addressFieldsVisible = this.shouldShowAddressFields();

        if (!this.checkout.addressFieldsVisible) {
            addressLookup.init('#delivery_address', {
                onFailed: () => this.showAddressFields(),
                onSuccess: (address) => this.handleAddressFound(address),
            });
        }

        if (this.userCan.changeDeliveryDate) {
            this.initVoucherDeliveryDateFields();
        }

        if (this.userCan.changeStartDate) {
            this.initVoucherStartDateFields();
        }

        this.checkDelivery();

        stripe.mountElement('#payment-element');

        this.startExpiryCheck();

        this.$watch('$store.basket.count', (count, previousCount) => {
            if (count === 0 && previousCount > 0 && !this.order.placed) {
                this.clearExpiryInterval();
                this.checkout.expired = true;
            }
        });

        this.$watch('order.bulk', (isBulk) => {
            if (!isBulk) return;

            Object.keys(this.vouchers).forEach(key => {
                this.vouchers[key].delivery = 'email';
                this.vouchers[key].delivery_method = 'email';
                this.vouchers[key].packaging = undefined;
                this.vouchers[key].packaging_id = undefined;
            });

            this.updateTotal();
        });

        formInput.init(this.$root);
    },

    // Step navigation

    changeStep(newStep) {
        if (this.checkout.admin) {
            this.checkout.failed = { data: { messages: [], next_url: null, status: '' }, status: false };
            return;
        }

        if (typeof newStep === 'string') {
            newStep = this.steps.find(step => step.name === newStep);
        }

        if (!newStep) return;

        const previousStep = this.steps[this.steps.indexOf(newStep) - 1];

        if (previousStep && !previousStep.valid) return;

        if (newStep.name === 'Billing') {
            this.checkout.stripe = { confirmationToken: '', errorMessage: '', paymentIntent: '', paymentMethod: null };
            this.steps.find(step => step.name === 'Billing').valid = false;
        }

        this.currentStep = newStep;
        this.checkout.failed = { data: { messages: [], next_url: null, status: '' }, status: false };
        this.checkout.ready = true;

        setTimeout(() => {
            document.querySelector('.step-indicator-wrapper')?.scrollIntoView();
        }, 100);
    },

    nextStep() {
        const nextStep = this.steps[this.steps.indexOf(this.currentStep) + 1];

        this.changeStep(nextStep);
        this.trackStepChange(nextStep);
    },

    previousStep() {
        this.changeStep(this.steps[this.steps.indexOf(this.currentStep) - 1]);
    },

    validateStep(stepName) {
        const formName = stepName || this.currentStep.name;
        const fieldset = this.checkout.form.querySelector(`[name="${formName}"]`);

        if (!fieldset) {
            this.nextStep();
            return;
        }

        const invalids = fieldset.querySelectorAll(':invalid');

        if (invalids.length > 0) {
            invalids.forEach(el => el.dataset.touched = 'true');

            if (this.order.requiresPosting && !this.checkout.addressFieldsVisible) {
                this.showAddressFields();
            }

            this.showFirstError();
            return;
        }

        const currentStep = this.steps.find(step => step.name === formName);

        if (currentStep) {
            currentStep.valid = true;
        }

        if (formName === 'Checkout' || (formName === 'Billing' && this.totals.full > 0)) {
            this.getStripeToken();
        } else {
            this.nextStep();
        }
    },

    showFirstError() {
        const formName = this.checkout.admin ? 'Checkout' : this.currentStep.name;

        setTimeout(() => {
            const errorEl = this.checkout.form.querySelector(`[name="${formName}"] :invalid`);

            if (errorEl) {
                errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    },

    trackStepChange(step) {
        if (!step) return;

        analytics.sendPageview(step.name);
        checkoutApi.upsertSession(this.checkout.session.type, this.checkout.form);

        const previousStep = this.steps[this.steps.indexOf(step) - 1];

        if (previousStep?.name === 'Delivery') {
            analytics.sendEcommerceEvent('add_shipping_info', this.$store.basket);
        } else if (step.name === 'Review' && this.checkout.stripe.paymentMethod) {
            analytics.sendEcommerceEvent('add_payment_info', this.$store.basket, {
                payment_type: `${this.checkout.stripe.paymentMethod.card.brand} ${this.checkout.stripe.paymentMethod.card.funding}`,
            });
        }
    },

    // Delivery

    checkDelivery() {
        const vouchers = Object.values(this.vouchers);

        const collectionItems = vouchers.filter(v => v.delivery === 'collect');
        const emailItems = vouchers.filter(v => v.delivery === 'email');
        const packagedPrintItems = vouchers.filter(v => v.delivery === 'print' && parseInt(v.packaging) > 0);
        const postalItems = vouchers.filter(v => v.delivery === 'post');
        const roomItems = vouchers.filter(v => v.delivery === 'room' && v.type === 'product');

        this.order.itemsToCollect = collectionItems.length;
        this.order.itemsToEmail = emailItems.length;
        this.order.itemsToFulfilInHouse = postalItems.filter(v => v.type !== 'product').length;
        this.order.itemsToPackage = postalItems.length + collectionItems.length + packagedPrintItems.length;
        this.order.itemsToPost = postalItems.length;
        this.order.itemsToRoom = roomItems.length;
        this.order.packagedItemsToPrint = packagedPrintItems.length;
        this.order.requiresCollection = this.order.itemsToCollect > 0;
        this.order.requiresPackaging = this.order.itemsToPackage > 0;
        this.order.requiresRoomDelivery = this.order.itemsToRoom > 0;

        const wasPosting = this.order.requiresPosting;
        this.order.requiresPosting = this.order.itemsToPost > 0;

        if (this.order.requiresPosting && !wasPosting) {
            this.updatePostageOptions();
        }

        if (!this.itemsCanBeCombined()) {
            this.order.combinePackagedVouchers = false;
        }

        this.updateTotal();
    },

    voucherCanBePackaged(voucher) {
        return ['collect', 'post', 'print'].includes(voucher.delivery) && voucher.packaging_option_ids.length > 0;
    },

    itemsCanBeCombined() {
        return this.order.itemsToPackage > 1;
    },

    toggleCombinedPackaging() {
        delivery.selectDefaultCombinedPackaging(this.order);
        this.updateTotal();
    },

    setDefaultPackagingOption(voucher) {
        if (voucher.delivery === 'collect' || voucher.delivery === 'post') {
            if (parseInt(voucher.packaging) === 0 || parseInt(voucher.packaging_id) === 0) {
                const ids = voucher.packaging_option_ids;
                const packaging = this.settings.packagingOptions.find(p => !!p.active && (!ids.length || ids.includes(p.id)));

                if (packaging) {
                    voucher.packaging = packaging.id;
                    voucher.packaging_id = packaging.id;
                }
            }
        } else {
            voucher.packaging = 0;
            voucher.packaging_id = 0;
        }

        this.checkDelivery();
    },

    filteredPostageOptions() {
        return delivery.filterPostageOptions(
            this.settings.postageOptions,
            this.delivery.country,
            this.settings.domesticCountries,
            this.settings.selfFulfilCountries,
        );
    },

    updatePostageOptions() {
        this.order.shippingOverseas = delivery.isShippingOverseas(this.delivery.country, this.settings.domesticCountries);

        const validOptions = this.filteredPostageOptions();

        if (validOptions.length) {
            // A different option changes the postage, so the total and Stripe amount must follow.
            const optionChanged = this.checkout.postageOption?.id !== validOptions[0].id;

            this.checkout.postageOption = validOptions[0];

            if (optionChanged) {
                this.updateTotal();
            }
        }
    },

    getPostageOption(id) {
        const postageOption = this.settings.postageOptions.find(option => option.id === id);

        if (postageOption) {
            this.checkout.postageOption = postageOption;
        }
    },

    // Address

    handleAddressFound(address) {
        delivery.populateAddressFields(this.delivery, address);
        this.updatePostageOptions();

        this.showAddressFields();

        setTimeout(() => {
            this.$root.querySelectorAll('.js-address-fields input').forEach(element => {
                formInput.hidePlaceholder(element);
            });
        }, 100);
    },

    showAddressFields() {
        this.checkout.addressFieldsVisible = true;
    },

    shouldShowAddressFields() {
        if (this.order.fulfilledByHotel) return true;
        if (!this.userCan.useAddressLookup) return true;

        const addressLookupCountries = ['GB', 'US'];

        return !this.settings.deliveryCountries.some(country => addressLookupCountries.includes(country));
    },

    // Personalisation

    getMessageLength(message) {
        if (message) {
            return Object.values(message).join('').length;
        }

        return 0;
    },

    watchVoucherMessageLength(key) {
        this.$watch(`vouchers.voucher${key}.message`, (newValue, oldValue) => {
            if (newValue) {
                if (this.getMessageLength(newValue) > this.settings.voucherMessageLimit) {
                    this.vouchers[`voucher${key}`].message = oldValue;
                }
            }
        });
    },

    displayVoucherMessage(voucher) {
        if (voucher && voucher.message) {
            return ((voucher.prefix || '') + ' ' + (voucher.name || '') + '\n' + (Object.values(voucher.message).join('\n') || '').trim())
                .replace(/(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g, '');
        }
    },

    voucherHasPreview(voucher) {
        if (voucher.delivery === 'email') return true;

        const packaging = this.settings.packagingOptions.find(option => option.id === parseInt(voucher.packaging));

        if (!packaging) return false;

        return packaging.has_voucher_preview_capability;
    },

    async previewVoucher(voucher) {
        if (!this.voucherHasPreview(voucher)) return;

        const modal = document.getElementById('voucherPreview');

        if (!modal) return;

        const iframe = modal.querySelector('iframe');
        const img = modal.querySelector('img');

        if (iframe) iframe.src = '';
        if (img) img.src = img.dataset.placeholder;

        modal.removeAttribute('data-type');
        window.jQuery(modal).foundation('reveal', 'open');

        const preview = await checkoutApi.previewVoucher(voucher);

        modal.setAttribute('data-type', preview.type);

        if (preview.type === 'iframe') {
            if (iframe) iframe.src = preview.data;
        } else {
            if (img) img.src = 'data:image/jpeg;base64,' + preview.data;
        }
    },

    // Date pickers

    initVoucherDeliveryDateFields() {
        setTimeout(() => {
            document.querySelectorAll('.js-voucher-delivery-date').forEach(element => {
                const maxDays = this.settings.scheduledDeliveryMaxDays || 84;

                dateTime.initDeliveryDatePicker(element, (date, el) => {
                    const key = el.dataset.voucherKey;
                    const model = this.vouchers[`voucher${key}`];

                    if (model) {
                        model.delivery_date = dateTime.formatISO(date);
                        model.delivery_time = '00:00';
                    }

                    const startDateEl = document.querySelector(`.js-voucher-start-date[data-voucher-key="${key}"]`);

                    if (startDateEl) {
                        dateTime.initStartDatePicker(startDateEl, date, (startDate, startEl) => {
                            const startKey = startEl.dataset.voucherKey;
                            const startModel = this.vouchers[`voucher${startKey}`];

                            if (startModel) {
                                startModel.valid_at = dateTime.formatISO(startDate);
                            }
                        }, maxDays);
                    }
                }, maxDays);
            });
        }, 0);
    },

    initVoucherStartDateFields() {
        setTimeout(() => {
            const maxDays = this.settings.scheduledDeliveryMaxDays || 84;

            document.querySelectorAll('.js-voucher-start-date').forEach(element => {
                dateTime.initStartDatePicker(element, null, (date, el) => {
                    const key = el.dataset.voucherKey;
                    const model = this.vouchers[`voucher${key}`];

                    if (model) {
                        model.valid_at = dateTime.formatISO(date);
                    }
                }, maxDays);
            });
        }, 0);
    },

    formatDate(date) {
        return dateTime.formatLocale(date);
    },

    // Audio recording

    async recordItemAudio(voucherKey) {
        if (!this.userCan.recordAudio) return;

        this.audioRecorder.errors = [];
        this.audioRecorder.state = 'init';
        this.audioRecorder.durationFormatted = '00:00';
        this.audioRecorder.ending = false;
        this._currentAudioVoucherKey = voucherKey;
        this._audioBlob = null;

        try {
            await audioRecorder.initRecorder();
            this.audioRecorder.state = 'record';

            window.jQuery('#audioRecorder').foundation('reveal', 'open');

            window.jQuery(document).on('closed.fndtn.reveal', '#audioRecorder', () => {
                this._destroyAudioRecording();
            });

            this._startWaveformLoop();
        } catch (error) {
            this.audioRecorder.errors = ['Could not access microphone'];
        }
    },

    startAudioRecording() {
        audioRecorder.start(() => {
            this.audioRecorder.state = 'recording';

            this._audioRecordingTimeout = setTimeout(() => {
                this.audioRecorder.ending = true;
            }, 25000);

            this._startDurationLoop();
        });
    },

    stopAudioRecording() {
        audioRecorder.stop((blob) => {
            if (!blob) return;

            this._audioBlob = blob;
            this.audioRecorder.state = 'review';
            this.audioRecorder.ending = false;

            const previewEl = document.querySelector('.js-audio-recorder-preview');
            if (previewEl) {
                previewEl.src = window.URL.createObjectURL(blob);
            }

            this._stopAnimationLoops();
        });
    },

    restartAudioRecording() {
        this._stopAnimationLoops();
        this.audioRecorder.durationFormatted = '00:00';
        this.audioRecorder.ending = false;
        this.audioRecorder.errors = [];
        this._audioBlob = null;

        audioRecorder.destroy();

        audioRecorder.initRecorder().then(() => {
            this.audioRecorder.state = 'record';
            this._startWaveformLoop();
        }).catch(() => {
            this.audioRecorder.errors = ['Could not access microphone'];
        });
    },

    async saveAudioRecording() {
        if (!this._audioBlob) return;

        try {
            const data = await audioRecorder.uploadAndSave(
                this.checkout.session.id,
                this._currentAudioVoucherKey,
                this._audioBlob,
            );

            const model = this.vouchers[`voucher${this._currentAudioVoucherKey}`];

            if (model && data.audio_path) {
                model.audio_path = data.audio_path;
                model.audio_url = window.URL.createObjectURL(this._audioBlob);
                this.audioRecorder.errors = [];
                window.jQuery('#audioRecorder').foundation('reveal', 'close');
            } else if (data.errors) {
                this.audioRecorder.errors = data.errors;
            }
        } catch (error) {
            this.audioRecorder.errors = ['Failed to save audio message'];
        }
    },

    _startWaveformLoop() {
        const waveforms = document.querySelectorAll('.js-audio-recorder-waveform');

        const update = () => {
            const data = audioRecorder.getAudioData();

            data.forEach((value, index) => {
                if (waveforms[index]) {
                    waveforms[index].style.height = ((value / 255) * 100) + '%';
                }
            });

            this._waveformAnimFrame = requestAnimationFrame(update);
        };

        this._waveformAnimFrame = requestAnimationFrame(update);
    },

    _startDurationLoop() {
        const update = () => {
            const duration = audioRecorder.getDuration();
            const date = new Date(0);
            date.setSeconds(duration);
            this.audioRecorder.durationFormatted = date.toISOString().substring(14, 19);

            if (duration >= 30) {
                this.stopAudioRecording();
                return;
            }

            this._durationAnimFrame = requestAnimationFrame(update);
        };

        this._durationAnimFrame = requestAnimationFrame(update);
    },

    _stopAnimationLoops() {
        if (this._waveformAnimFrame) cancelAnimationFrame(this._waveformAnimFrame);
        if (this._durationAnimFrame) cancelAnimationFrame(this._durationAnimFrame);
        if (this._audioRecordingTimeout) clearTimeout(this._audioRecordingTimeout);
    },

    _destroyAudioRecording() {
        this._stopAnimationLoops();
        audioRecorder.destroy();
        this.audioRecorder.state = 'init';
        this._audioBlob = null;
        window.jQuery(document).off('closed.fndtn.reveal', '#audioRecorder');
    },

    // Billing

    async updateTotal(overrideTotals = false) {
        this.checkout.totalUpdating = true;
        this._totalManager.updateTotal(overrideTotals);
    },

    async applyPromoCode() {
        this.checkout.promoCode.pending = true;
        await this._totalManager.applyPromoCode(this.checkout.promoCode.value);
        this.checkout.promoCode.pending = false;
    },

    async removePromoCode() {
        this.checkout.promoCode.pending = true;
        await this._totalManager.removePromoCode();
        this.checkout.promoCode.pending = false;
        this.checkout.promoCode.value = '';

        setTimeout(() => {
            const promoInput = this.$root.querySelector('[name="promocode"]');

            if (promoInput) {
                formInput.showPlaceholder(promoInput);
            }
        }, 100);
    },

    // Stripe + submission

    stripeTokenRequired() {
        if (this.totals.full === 0) return false;
        if (this.checkout.paymentMethod !== 'card_gifted') return false;

        return this.checkout.stripe.confirmationToken === '';
    },

    async getStripeToken() {
        if (!this.stripeTokenRequired()) {
            this.placeOrder();
            return;
        }

        this.checkout.ready = false;

        const { confirmationToken, error } = await stripe.getToken({
            email: this.contact.email,
            name: `${this.contact.firstName} ${this.contact.lastName}`,
            phone: this.contact.telephone,
        });

        if (!confirmationToken || error) {
            this.checkout.ready = true;

            if (error && error.type !== 'validation_error') {
                this.checkout.stripe.errorMessage = error.message;
            }

            return;
        }

        this.checkout.stripe.confirmationToken = confirmationToken.id;
        this.checkout.stripe.paymentMethod = confirmationToken.payment_method_preview;

        if (this.checkout.admin) {
            this.checkout.ready = true;
            this.placeOrder();
        } else {
            this.nextStep();
        }
    },

    async placeOrder() {
        if (!this.checkout.ready) return;

        this.checkout.ready = false;
        this.order.placed = true;
        this.checkout.placementStage = 'placing';

        if (this.stripeTokenRequired()) {
            await this.getStripeToken();
            return;
        }

        const formData = new URLSearchParams(new FormData(this.checkout.form));

        formData.append('checkout_session_type', this.checkout.session.type);
        formData.append('checkout_token', this.checkout.token);
        formData.append('payment_intent_id', this.checkout.stripe.paymentIntent);
        formData.append('stripe_token', this.checkout.stripe.confirmationToken);

        if (this.checkout.stripe.paymentMethod) {
            formData.append('stripe_country', this.checkout.stripe.paymentMethod.billing_details?.address?.country || '');
        }

        if (this.vouchers) {
            Object.values(this.vouchers).forEach((voucher, key) => {
                if (voucher.audio_path) {
                    formData.append(`vouchers[${key}][audio_path]`, voucher.audio_path);
                }
            });
        }

        if (this.checkout.admin) {
            formData.append('override_totals', '1');
        }

        try {
            const data = await checkoutApi.submit(this.checkout.form, formData);

            this.checkout.placementStage = 'finalising';
            this.awaitOrderCreation(data.session_id);
        } catch (data) {
            if (data.status === 'PAYMENT_REQUIRES_ACTION') {
                const { error, paymentIntent } = await orderSubmission.handlePaymentAction(data.payment.client_secret);

                if (!paymentIntent || error) {
                    this.checkout.stripe = { confirmationToken: '', errorMessage: '', paymentIntent: '', paymentMethod: null };
                    this.order.placed = false;
                    this.checkout.ready = true;
                    this.checkout.failed = {
                        data: { messages: [error?.message], next_url: null, status: 'PAYMENT_FAILED' },
                        status: true,
                    };
                    return;
                }

                this.checkout.stripe.paymentIntent = paymentIntent.id;
                this.checkout.ready = true;

                setTimeout(() => this.placeOrder(), 500);
            } else {
                const action = orderSubmission.decideNextAction(data, { hasPaymentIntent: !!this.checkout.stripe.paymentIntent });

                if (action.type === 'await') {
                    this.checkout.placementStage = 'finalising';
                    this.awaitOrderCreation(action.sessionId || this.checkout.session.id);
                    return;
                }

                this.checkout.ready = true;
                this.order.placed = false;

                const messages = data.messages || (data.message ? [data.message] : ['Something went wrong. Please try again.']);

                this.checkout.failed = { data: { ...data, messages }, status: true };
            }
        }
    },

    awaitOrderCreation(sessionId) {
        orderSubmission.awaitOrderCreation(sessionId, {
            onComplete: (session) => {
                window.location.pathname = new URL(session.redirect_url).pathname;
            },
            onFailed: (data) => {
                this.order.placed = false;
                this.checkout.failed = { data, status: true };
            },
            onTimeout: () => {
                // Payment succeeded; rest on the 'finalising' reassurance copy. The
                // confirmation email is sent regardless, so we never show an error here.
            },
        });
    },

    // Expiry check

    startExpiryCheck() {
        this._expiryInterval = setInterval(async () => {
            if (this.order.placed) return;

            const token = await checkoutApi.getToken();

            if (!this.order.placed && this.checkout.token !== token) {
                this.clearExpiryInterval();
                this.checkout.expired = true;
            }
        }, 5000);

        analytics.sendEcommerceEvent('begin_checkout', this.$store.basket);
    },

    clearExpiryInterval() {
        if (this._expiryInterval) {
            clearInterval(this._expiryInterval);
            this._expiryInterval = null;
        }
    },

    // Utility

    orderCanBeFulfilled() {
        if (!this.order.requiresPosting) return true;
        if (this.order.fulfilledByHotel) return true;

        return this.checkout.paymentMethod === 'card_gifted';
    },
});
